'use client';

import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { TextButtonProps } from '../button/types.js';
import type { StringLikeChildren } from '../types.js';
import { InternalToggleButton, type BaseToggleButtonProps } from './internal-toggle-button.js';

export interface ToggleButtonProps extends BaseToggleButtonProps, Omit<TextButtonProps, 'children'> {
  /** The text displayed on the button. Must remain the same regardless of selection state. */
  children: StringLikeChildren;
}

function ToggleButton(
  { children, iconStart, iconEnd, UNSAFE_className, ...props }: ToggleButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <InternalToggleButton {...props} buttonRef={ref} UNSAFE_className={clsx('cim-button', UNSAFE_className)}>
      {iconStart}
      <span className="cim-button-text">{children}</span>
      {iconEnd}
    </InternalToggleButton>
  );
}

/**
 * Displays a labelled button that allows users to toggle between two states.
 * Can be used standalone, or as part of `ToggleButtonGroup`.
 *
 * See [toggle button usage guidelines](https://ui.cimpress.io/components/toggle-button/).
 */
const _ToggleButton = withStyleProps(forwardRef(ToggleButton), 'ToggleButton');

export { _ToggleButton as ToggleButton };
